import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/hooks/useUserProfile";

export type PlannerActivity = 'viewing' | 'editing';

export interface PlannerViewer {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  online_at: string;
  activity: PlannerActivity;
}

/**
 * Tracks who else is currently viewing the tank planner using Supabase
 * channel presence (WebSocket-based, no DB writes needed).
 *
 * Returns:
 *   self           — current user's viewer record (always present when logged in)
 *   viewers        — OTHER users currently viewing (excludes self)
 *   updateActivity — call with 'editing' when a popover/dialog opens, 'viewing' on close
 */
export function usePlannerPresence() {
  const { user } = useAuth();
  const { data: profile } = useUserProfile();
  const [viewers, setViewers] = useState<PlannerViewer[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  // Ref so updateActivity closure always has the latest values without re-creating the channel.
  const activityRef = useRef<PlannerActivity>('viewing');
  const profileRef = useRef({ display_name: '', avatar_url: null as string | null });

  const self = useMemo<PlannerViewer | null>(() => {
    if (!user) return null;
    return {
      user_id: user.id,
      display_name: profile?.display_name || user.email?.split("@")[0] || "Unknown",
      avatar_url: profile?.avatar_url ?? null,
      online_at: new Date().toISOString(),
      activity: activityRef.current,
    };
  }, [user?.id, profile?.display_name, profile?.avatar_url]);

  // Keep profileRef in sync so updateActivity can read current values.
  useEffect(() => {
    profileRef.current = {
      display_name: profile?.display_name || user?.email?.split("@")[0] || "Unknown",
      avatar_url: profile?.avatar_url ?? null,
    };
  }, [profile?.display_name, profile?.avatar_url, user?.email]);

  // Create the channel once on mount and subscribe to presence events.
  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel("tank-planning-presence", {
      config: { presence: { key: user.id } },
    });
    channelRef.current = channel;

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState<PlannerViewer>();
        const others: PlannerViewer[] = Object.values(state)
          .flatMap((presences) => presences as PlannerViewer[])
          .filter((p) => p.user_id !== user.id);
        setViewers(others);
      })
      .subscribe(async (status) => {
        if (status !== "SUBSCRIBED") return;
        await channel.track({
          user_id: user.id,
          display_name: user.email?.split("@")[0] ?? "Unknown",
          avatar_url: null,
          online_at: new Date().toISOString(),
          activity: 'viewing' as PlannerActivity,
        });
      });

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [user?.id]);

  // Re-track when profile loads/changes, preserving current activity.
  useEffect(() => {
    if (!channelRef.current || !user || !profile) return;
    channelRef.current.track({
      user_id: user.id,
      display_name: profile.display_name || user.email?.split("@")[0] || "Unknown",
      avatar_url: profile.avatar_url ?? null,
      online_at: new Date().toISOString(),
      activity: activityRef.current,
    });
  }, [profile?.display_name, profile?.avatar_url, user?.id]);

  // Call this from TankPlanningView when the user starts/stops editing.
  const updateActivity = useCallback((activity: PlannerActivity) => {
    if (!channelRef.current || !user) return;
    activityRef.current = activity;
    channelRef.current.track({
      user_id: user.id,
      display_name: profileRef.current.display_name,
      avatar_url: profileRef.current.avatar_url,
      online_at: new Date().toISOString(),
      activity,
    });
  }, [user?.id]);

  return { self, viewers, updateActivity };
}
